/**
 * Counting the two things this site exists to cause: a share and a copied embed snippet.
 *
 * The whole implementation is "call `gtag` if a `gtag` is there". `public/ga.js` defines it, and
 * `SpaShellMiddleware` only loads that file when a measurement id is configured — so on a
 * development host, in every test and on a deployment that never configured analytics this is a
 * no-op, which is why nothing here throws and nothing here waits.
 *
 * **Counts, never identifiers.** The parameters below are a format (`markdown`), a page target
 * (`openai`) and a channel (`x`) — cardinality in the single digits, nothing about a person, no
 * cookie (`ga.js` denies `analytics_storage` unless the deployment turns it on). That is the same
 * promise the About page makes, and this is one of the two places it could quietly be broken.
 *
 * It matters because X03 reads these: "does anybody actually paste the badge" is a question the
 * site cannot answer from server logs — a copy happens entirely in the browser, and the badge
 * request that follows it arrives days later from somebody else's README.
 */

type Gtag = (command: 'event', name: string, params: Record<string, string>) => void

/**
 * One counted event.
 *
 * Read off `window` at call time rather than captured at module load: `ga.js` is `defer`red and the
 * tag library is `async`, so a share in the first second of a page's life would otherwise be
 * counted against a `gtag` that did not exist yet — and, worse, would be counted against a stale
 * reference for the rest of the session.
 */
export function track(name: string, params: Record<string, string>): void {
  const gtag = (globalThis as { gtag?: Gtag }).gtag
  if (typeof gtag !== 'function') return
  try {
    gtag('event', name, params)
  } catch {
    // Analytics is never the reason a copy button or a share link fails. An ad blocker that
    // replaced `gtag` with something that throws is the common case.
  }
}
